import { useState, type FC } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import type { AxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import { InlineLoader } from '~/shared/ui'
import {
  useChangeableAppointmentTypes,
  useChangeAppointmentType,
  type Appointment,
} from './PatientAppointmentsService'

interface ChangeAppointmentTypeModalProps {
  appointment: Appointment
  patientId: number
  onClose: () => void
}

/**
 * Re-types a booked appointment. The server lists the types the member may change to, each
 * with the length it would give this member, and says why a change is refused.
 */
export const ChangeAppointmentTypeModal: FC<ChangeAppointmentTypeModalProps> = ({
  appointment,
  patientId,
  onClose,
}) => {
  const [reason, setReason] = useState('')
  const { enqueueSnackbar } = useSnackbar()
  const options = useChangeableAppointmentTypes(appointment.id)
  const changeType = useChangeAppointmentType(patientId)
  const appointmentTypes = options.data?.appointment_types ?? []
  const currentLabel = appointment.appointment_type?.name ?? appointment.reason

  const submit = () =>
    changeType.mutate(
      { appointmentId: appointment.id, reason },
      {
        onSuccess: () => {
          enqueueSnackbar('Visit type changed', { variant: 'success' })
          onClose()
        },
        onError: error => {
          const detail = (error as AxiosError<{ detail?: string }>).response?.data?.detail
          enqueueSnackbar(detail ?? 'Failed to change the visit type', { variant: 'error' })
        },
      },
    )

  const renderOptions = () => {
    if (options.isPending) return <InlineLoader />
    if (appointmentTypes.length === 0) {
      return <Typography>No other visit types are available for this member.</Typography>
    }
    return (
      <RadioGroup value={reason} onChange={event => setReason(event.target.value)}>
        {appointmentTypes.map(appointmentType => (
          <FormControlLabel
            key={appointmentType.unique_key}
            value={appointmentType.unique_key}
            control={<Radio />}
            label={`${appointmentType.name} (${appointmentType.duration_minutes} min)`}
          />
        ))}
      </RadioGroup>
    )
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Change visit type</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Current: {currentLabel} ({appointment.duration_minutes} min)
        </Typography>
        {renderOptions()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={changeType.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={reason === '' || changeType.isPending}
        >
          Change visit type
        </Button>
      </DialogActions>
    </Dialog>
  )
}
